import { Box, Flex, Stack, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import useCheckMyProfile from '../../../../../../hooks/useCheckMyProfile';
import useInstructorProfileQuery from '../../../../../../hooks/useInstructorProfileQuery';
import useQueryParams from '../../../../../../hooks/useQueryParams';
import useUserProfileQuery from '../../../../../../hooks/useUserProfileQuery';
import useClientAccountProfileQuery from '../../../../../../queries/useClientAccountProfileQuery';
import { formatCommaNumber } from '../../../../../../utils/format/number';
import { AvatarImage } from '../AvatarImage';
import { FollowingButton } from '../FollowingButton';
import { HubProfile } from '../HubProfile';
import { ProfileEditButton } from '../ProfileEditButton';
import { RatingItem } from '../RatingItem';
import { StatItem } from '../StatItem';
import { UserName } from '../UserName';
import { UserSlug } from '../UserSlug';
import { UserSlugModal } from '../UserSlugModal';
import { MentoringApplyButton } from './MentoringApplyButton';
import { MentoringEditButton } from './MentoringEditButton';
import { MentoringInformation } from './MentoringInformation';

export function InstructorProfile() {
  const [isSlugModalOpened, slugModalHandler] = useDisclosure(false);
  const { id } = useQueryParams();
  const { isMe } = useCheckMyProfile();
  const { data: profile } = useUserProfileQuery({ id: Number(id) });
  const { data: myProfile } = useClientAccountProfileQuery({ enabled: isMe });
  const { data: instructorProfile } = useInstructorProfileQuery({
    userId: String(id),
    isInstructor: true,
  });
  const { t } = useTranslation();
  const { isKo } = useLang();

  useEffect(() => {
    if (myProfile && !myProfile.isSlugConfirmed) {
      slugModalHandler.open();
    }
  }, [myProfile]);

  if (!profile || !instructorProfile) {
    return null;
  }

  const isActiveMentor = isKo && Boolean(instructorProfile.mentor?.isActive);

  return (
    <>
      <Stack spacing={24}>
        <Flex
          justify="space-between"
          css={{
            [desktopFirstMediaQuery.mobile]: {
              flexDirection: 'column',
              gap: 24,
            },
          }}
        >
          <Flex gap={24} align="center">
            <Box css={{ flexShrink: 0 }}>
              <AvatarImage src={profile.thumbnailUrl} />
            </Box>
            <Stack spacing={10}>
              <Box>
                <UserName name={profile.name} />
                <UserSlug slug={profile.slug} />
                <MentoringInformation />
              </Box>
              <HubProfile userId={profile.id} userName={profile.name} />
            </Stack>
          </Flex>
          <Flex gap={10}>
            {isMe && <ProfileEditButton />}
            {isMe && isActiveMentor && <MentoringEditButton />}
            {!isMe && <FollowingButton />}
            {!isMe && isActiveMentor && <MentoringApplyButton />}
          </Flex>
        </Flex>
        <Box
          css={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 12,
          }}
        >
          <StatItem
            label={t('수강생')}
            content={
              instructorProfile.studentCount === 0
                ? '-'
                : formatCommaNumber(instructorProfile.studentCount)
            }
          />
          <StatItem
            label={t('수강평')}
            content={
              instructorProfile.reviewCount === 0
                ? '-'
                : formatCommaNumber(instructorProfile.reviewCount)
            }
          />
          <StatItem
            label={t('강의 평점')}
            content={
              instructorProfile.reviewAverage === '0.0' ? (
                '-'
              ) : (
                <RatingItem content={instructorProfile.reviewAverage} />
              )
            }
          />

          {isActiveMentor && (
            <>
              <StatItem
                label={t('멘토링 신청')}
                content={
                  !instructorProfile.mentor?.mentoringCount ||
                  instructorProfile.mentor.mentoringCount === 0
                    ? '-'
                    : formatCommaNumber(instructorProfile.mentor.mentoringCount)
                }
              />
              <StatItem
                label={t('멘토링 리뷰')}
                content={
                  !instructorProfile.mentor?.reviewCount ||
                  instructorProfile.mentor.reviewCount === 0
                    ? '-'
                    : formatCommaNumber(instructorProfile.mentor.reviewCount)
                }
              />
              <StatItem
                label={t('멘토링 평점')}
                content={
                  !instructorProfile.mentor?.reviewAverage ||
                  instructorProfile.mentor.reviewAverage === '0.0' ? (
                    '-'
                  ) : (
                    <RatingItem content={instructorProfile.mentor.reviewAverage} />
                  )
                }
              />
            </>
          )}
        </Box>
      </Stack>
      {myProfile && (
        <UserSlugModal
          userId={myProfile.id}
          nickname={myProfile.name}
          thumbnailUrl={myProfile.thumbnail}
          initialSlug={myProfile.slug || ''}
          isOpened={isSlugModalOpened}
          onClose={slugModalHandler.close}
        />
      )}
    </>
  );
}
